import { useState, useCallback, useEffect, useRef } from "react";
import useLatest from "./useLatest";
import _debounce from "../helpers/debounce";

export interface HookArgs {
  requestOptions?: Omit<google.maps.places.AutocompletionRequest, "input">;
  debounce?: number;
  cache?: number | false;
  cacheKey?: string;
  googleMaps?: { places?: typeof google.maps.places; maps?: typeof google.maps };
  callbackName?: string;
  defaultValue?: string;
  initOnMount?: boolean;
}

type Suggestion = google.maps.places.AutocompleteSuggestion;

type Status = `${google.maps.places.PlacesServiceStatus}` | "";

interface Suggestions {
  readonly loading: boolean;
  readonly status: Status;
  data: Suggestion[];
}

type SetValue = (val: string, shouldFetchData?: boolean) => void;

/** Type for globalThis when used to register the Places init callback by name (deferred script load). */
type GlobalWithCallback = typeof globalThis & Record<string, (() => void) | undefined>;

interface HookReturn {
  ready: boolean;
  value: string;
  suggestions: Suggestions;
  setValue: SetValue;
  clearSuggestions: () => void;
  clearCache: (key?: string) => void;
  init: () => void;
}

export const loadApiErr =
  "💡 use-places-autocomplete: Google Maps Places API library must be loaded. See: https://github.com/wellyshen/use-places-autocomplete#load-the-library";

const usePlacesAutocomplete = ({
  requestOptions,
  debounce = 200,
  cache = 24 * 60 * 60,
  cacheKey = "upa-v3",
  googleMaps,
  callbackName,
  defaultValue = "",
  initOnMount = true,
}: HookArgs = {}): HookReturn => {
  const [ready, setReady] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<Suggestions>({
    loading: false,
    status: "",
    data: [],
  });
  const requestOptionsRef = useLatest(requestOptions);
  const googleMapsRef = useLatest(googleMaps);
  /** In-memory cache only. SessionStorage would JSON.stringify suggestions and break them
   * (Google returns class instances with getters; stringify keeps internal keys like mh, AC, KB). */
  const memoryCacheRef = useRef<
    Record<string, { data: google.maps.places.AutocompleteSuggestion[]; maxAge: number }>
  >({});

  const init = useCallback(() => {
    const { google } = globalThis;
    const { current: gMaps } = googleMapsRef;
    const placesLib = gMaps?.places ?? google?.maps?.places;

    if (!placesLib) {
      console.error(loadApiErr);
      return;
    }

    setReady(true);
  }, [googleMapsRef]);

  const clearSuggestions = useCallback(() => {
    setSuggestions({ loading: false, status: "", data: [] });
  }, []);

  const clearCache = useCallback(() => {
    memoryCacheRef.current = {};
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPredictions = useCallback(
    _debounce(
      (async (val: string) => {
        if (!val) {
          clearSuggestions();
          return;
        }

        setSuggestions((prevState) => ({ ...prevState, loading: true }));

        let cachedData = memoryCacheRef.current;

        if (cache) {
          const now = Date.now();
          cachedData = Object.keys(cachedData).reduce((acc: typeof cachedData, key) => {
            if (cachedData[key].maxAge - now >= 0) acc[key] = cachedData[key];
            return acc;
          }, {});
          memoryCacheRef.current = cachedData;

          if (cachedData[val]) {
            setSuggestions({
              loading: false,
              status: "OK",
              data: cachedData[val].data,
            });
            return;
          }
        }

        const { google } = globalThis;
        const placesLib = google?.maps?.places;

        if (!placesLib) return;

        try {
          const request = {
            input: val,
            ...requestOptionsRef.current,
          } as google.maps.places.AutocompleteRequest;

          // Use the new AutocompleteSuggestion API
          const { suggestions: newSuggestions } =
            await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          const data = newSuggestions ?? [];

          setSuggestions({ loading: false, status: "OK", data });

          if (cache) {
            memoryCacheRef.current[val] = {
              data,
              maxAge: Date.now() + cache * 1000,
            };
          }
        } catch (error) {
          console.warn("[usePlacesAutocomplete] fetchAutocompleteSuggestions failed", error);
          setSuggestions({ loading: false, status: "ZERO_RESULTS", data: [] });
        }
      }) as (val: string) => void,
      debounce
    ),
    [cache, clearSuggestions, requestOptionsRef]
  );

  const setValueWithPredictions: SetValue = useCallback(
    (val, shouldFetchData = true) => {
      setValue(val);
      if (shouldFetchData) fetchPredictions(val);
    },
    [fetchPredictions]
  );

  useEffect(() => {
    if (!initOnMount) return () => null;

    const { google } = globalThis;

    if (!googleMapsRef.current && !google?.maps && callbackName) {
      (globalThis as GlobalWithCallback)[callbackName] = init;
    } else {
      init();
    }

    return () => {
      const global = globalThis as GlobalWithCallback;
      if (callbackName && global[callbackName]) global[callbackName] = undefined;
    };
  }, [callbackName, googleMapsRef, init, initOnMount]);

  return {
    ready,
    value,
    suggestions,
    setValue: setValueWithPredictions,
    clearSuggestions,
    clearCache,
    init,
  };
};

export default usePlacesAutocomplete;
